import React, { useEffect, useRef, useState } from "react";
import { Button, ConfigProvider, Empty, Input, List, Modal, Space, Table, Tag } from "antd";
import type { InputRef, TableColumnType, TableProps } from "antd";
import { FilterDropdownProps } from "antd/es/table/interface";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import { fetchUsersWithProjects } from "../../store/slices/userSlice";
import { useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import { useSelector } from "react-redux";
import { Projet } from "../../domaine/userModel";
import { retirerProject } from "../../store/slices/projectSlice";

interface DataType {
  key: string;
  visiteur: string;
  nom_du_projet: string[];
}

type DataIndex = keyof DataType;

const Disponibilite_projet: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { usersWithProjects } = useSelector((state: RootState) => state.user);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedProjects, setSelectedProjects] = useState<Projet[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef<InputRef>(null);

  const handleOk = () => {
    setIsModalVisible(false);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const transformedData: DataType[] = usersWithProjects.map((user) => {
    const userProjects = user.projets;
    return {
      key: user.id.toString(),
      visiteur: `${user.nom} ${user.prenom}`,
      nom_du_projet: userProjects.map((projet) => projet.nom),
    };
  });

  const handleSearch = (
    selectedKeys: string[],
    confirm: FilterDropdownProps["confirm"],
    dataIndex: DataIndex
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (
    dataIndex: DataIndex
  ): TableColumnType<DataType> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({ closeDropdown: false });
              setSearchText((selectedKeys as string[])[0]);
              setSearchedColumn(dataIndex);
            }}
          >
            Filter
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const columns: TableProps<DataType>["columns"] = [
    {
      title: "Nom visiteur",
      dataIndex: "visiteur",
      key: "visiteur",
      render: (text) => <a>{text}</a>,
      ...getColumnSearchProps("visiteur"),
    },
    {
      title: "Disponibilité",
      dataIndex: "nom_du_projet",
      key: "nom_du_projet",
      sorter: (a, b) => b.nom_du_projet.length - a.nom_du_projet.length,
      defaultSortOrder: "ascend",
      render: (_, { nom_du_projet: tags, key }) => {
        let color = "green";
        if (tags.length > 5) {
          color = "red";
        } else if (tags.length >= 2 && tags.length <= 5) {
          color = "orange";
        }

        return (
          <Tag
            color={color}
            onClick={() => {
              const selectedUser = usersWithProjects.find(
                (user) => user.id.toString() === key
              );
              if (selectedUser) {
                setSelectedProjects(selectedUser.projets);
                setSelectedUserId(key);
                setIsModalVisible(true);
              }
            }}
            style={{ cursor: "pointer" }}
          >
            {`${tags.length} Projets à visiter`}
          </Tag>
        );
      },
    },
  ];

  const handleRemoveProject = async (idProjet: number) => {
    if (selectedUserId) {
      await dispatch(retirerProject({ idProjet, idUser: parseInt(selectedUserId) }));
      const selectedUser = usersWithProjects.find(
        (user) => user.id.toString() === selectedUserId
      );
      if (selectedUser) {
        setSelectedProjects(selectedUser.projets);
        setIsModalVisible(false);
      }
    }
  };

  useEffect(() => {
    dispatch(fetchUsersWithProjects());
  }, [dispatch]);

  return (
    <>
      <Table<DataType>
        size="small"
        columns={columns}
        dataSource={transformedData}
        pagination={{ pageSize: 5 }}
      />
      <Modal
        title="Liste des Projets affectés"
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        footer={null}
        closable
      >
        <ConfigProvider renderEmpty={() => <Empty description="Aucun projet affecté" />}>
          <List
            dataSource={selectedProjects}
            renderItem={(project: Projet) => (
              <List.Item
                actions={[
                  <Button
                    type="link"
                    danger
                    onClick={() => handleRemoveProject(project.id)}
                  >
                    Retirer
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  title={`Projet: ${project.nom}`}
                  description={`Type: ${project.type_realisation}, État: ${project.etat_projet}`}
                />
              </List.Item>
            )}
          />
        </ConfigProvider>
      </Modal>
    </>
  );
};

export default Disponibilite_projet;